import { useCallback, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

import type { ReactNode } from 'react';
import type { Root } from 'react-dom/client';

interface TooltipRoots {
  renderTooltip: (container: Element, content: ReactNode) => void;
  unmountTooltips: () => void;
}

/**
 * Renders React content into detached marker tooltips and holds on to the roots
 * it creates. Markers are cleared and redrawn on every planning update, so
 * their roots have to be unmounted along with them, otherwise every redraw
 * leaves a mounted root behind for the lifetime of the map.
 */
export default function useTooltipRoots(): TooltipRoots {
  const roots = useRef<Root[]>([]);

  const unmountTooltips = useCallback(() => {
    roots.current.forEach((root) => root.unmount());
    roots.current = [];
  }, []);

  const renderTooltip = useCallback(
    (container: Element, content: ReactNode) => {
      const root = createRoot(container);
      roots.current.push(root);

      // Note: using flushSync with createRoot to avoid using the heavy
      // react-dom/server package. Additionally, in React 18 MUI's styling is not
      // added during transpilation.
      flushSync(() => root.render(content));
    },
    []
  );

  useEffect(() => unmountTooltips, [unmountTooltips]);

  return { renderTooltip, unmountTooltips };
}
